import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import type { FormEvent } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  Link as RouterLink,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { ApiError, authStore, buildQuery } from "./client";
import type { QueryParams } from "./client";
import { ApiErrorPanel, useApi, useBrandScope } from "./shared";
import { DataValue } from "../shared/presentation";
import { isCopyableField, isDateTimeField } from "../shared/format";
import {
  entityConfig,
  fieldLabel,
  filterViolationMessages,
  pagingForQuery,
  validateDates,
} from "./entityConfig";
import type {
  EntityConfig,
  EntityName,
  EntityRecord,
  FilterValues,
  PagingState,
} from "./entityConfig";

interface CursorPage {
  items: EntityRecord[];
  nextCursor?: string | null;
}

interface OffsetPage {
  items: EntityRecord[];
  page: number;
  size: number;
  totalItems: number;
}

function readFilters(
  config: EntityConfig,
  query: URLSearchParams,
): FilterValues {
  return Object.fromEntries(
    config.filters.map((filter) => [
      filter.name,
      filter.multiple
        ? query.getAll(filter.name)
        : query.get(filter.name) || "",
    ]),
  );
}

function validPageSize(value: string | null): number {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 1 && parsed <= 200 ? parsed : 50;
}

function brandOf(record: EntityRecord, fallback?: number): number | undefined {
  const candidate = Number(record.brandId);
  return record.brandId != null &&
    Number.isSafeInteger(candidate) &&
    candidate > 0
    ? candidate
    : fallback;
}

function listLink(entity: EntityName, params: QueryParams): string {
  return `/${entity}${buildQuery(params)}`;
}

function detailLink(
  entity: EntityName,
  record: EntityRecord,
  brandId?: number,
): string {
  const id = record[entityConfig[entity].idField];
  return `/${entity}/${encodeURIComponent(String(id))}${buildQuery({ brandId: brandOf(record, brandId) })}`;
}

function EntityValue({
  entity,
  field,
  record,
  brandId,
  linkId = false,
}: {
  entity: EntityName;
  field: string;
  record: EntityRecord;
  brandId?: number;
  linkId?: boolean;
}) {
  const value = record[field];
  const valueProps = {
    value,
    label: fieldLabel(field),
    copy: isCopyableField(field),
    date: isDateTimeField(field, value),
    maxWidth: linkId ? 260 : 640,
  };
  if (value === undefined || value === null || value === "")
    return <DataValue {...valueProps} />;
  if (linkId && field === entityConfig[entity].idField) {
    return (
      <DataValue {...valueProps} to={detailLink(entity, record, brandId)} />
    );
  }
  if (entity === "transactions" && field === "sessionToken") {
    return (
      <DataValue
        {...valueProps}
        to={listLink("sessions", {
          sessionToken: String(value),
          brandId: brandOf(record, brandId),
        })}
      />
    );
  }
  if (field === "status")
    return <Chip label={String(value)} size="small" variant="outlined" />;
  if (typeof value === "boolean")
    return (
      <Chip
        label={value ? "Yes" : "No"}
        size="small"
        variant="outlined"
        color={value ? "success" : "default"}
      />
    );
  if (
    ["gameUrl", "imageLink", "returnUrl", "accountHistoryUrl"].includes(
      field,
    ) &&
    typeof value === "string" &&
    /^https?:\/\//i.test(value)
  ) {
    return <DataValue {...valueProps} href={value} />;
  }
  return <DataValue {...valueProps} />;
}

function Filters({
  config,
  applied,
  onApply,
  onReset,
  apiError,
}: {
  config: EntityConfig;
  applied: FilterValues;
  onApply: (values: FilterValues) => void;
  onReset: () => void;
  apiError?: unknown;
}) {
  const [draft, setDraft] = useState(applied);
  const [error, setError] = useState<string>();
  const [localFieldErrors, setLocalFieldErrors] = useState<
    Record<string, string>
  >({});
  const apiFieldErrors =
    apiError instanceof ApiError
      ? filterViolationMessages(apiError.violations)
      : {};
  const [expanded, setExpanded] = useState(true);
  const serialized = JSON.stringify(applied);
  useEffect(() => {
    setDraft(JSON.parse(serialized) as FilterValues);
    setError(undefined);
    setLocalFieldErrors({});
  }, [serialized]);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const checked = validateDates(config, draft);
    setError(checked.error);
    setLocalFieldErrors(checked.fieldErrors || {});
    if (!checked.error) onApply(checked.values);
  };
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={expanded ? 2 : 0}
      >
        <Typography variant="subtitle2">Filters</Typography>
        <FormControlLabel
          label="Show filters"
          control={
            <Switch
              checked={expanded}
              onChange={(_, checked) => setExpanded(checked)}
              size="small"
            />
          }
          sx={{ mr: 0 }}
        />
      </Stack>
      <Collapse in={expanded}>
        <Box component="form" onSubmit={submit}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 2,
            }}
          >
            {config.filters.map((filter) => {
              const unchanged =
                JSON.stringify(draft[filter.name]) ===
                JSON.stringify(applied[filter.name]);
              const message =
                localFieldErrors[filter.name] ||
                (unchanged ? apiFieldErrors[filter.name] : undefined);
              return (
                <TextField
                  key={filter.name}
                  label={filter.label}
                  size="small"
                  fullWidth
                  error={!!message}
                  select={!!filter.options}
                  value={draft[filter.name] ?? (filter.multiple ? [] : "")}
                  onChange={(event) => {
                    const value = event.target.value;
                    setLocalFieldErrors((current) => ({
                      ...current,
                      [filter.name]: "",
                    }));
                    setDraft((current) => ({
                      ...current,
                      [filter.name]:
                        filter.multiple && typeof value === "string"
                          ? value.split(",").filter(Boolean)
                          : value,
                    }));
                  }}
                  SelectProps={filter.multiple ? { multiple: true } : undefined}
                  placeholder={filter.date ? "2026-11-04T12:00:00Z" : undefined}
                  helperText={
                    message ||
                    (filter.date
                      ? "ISO 8601 with timezone; sent as UTC"
                      : undefined)
                  }
                >
                  {filter.options && !filter.multiple && (
                    <MenuItem value="">All</MenuItem>
                  )}
                  {filter.options?.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              );
            })}
          </Box>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          <Stack direction="row" gap={1} mt={2}>
            <Button type="submit" variant="contained">
              Apply filters
            </Button>
            <Button type="button" onClick={onReset}>
              Reset filters
            </Button>
          </Stack>
        </Box>
      </Collapse>
    </Paper>
  );
}

function EntityList({ entity }: { entity: EntityName }) {
  const config = entityConfig[entity];
  const { brandId } = useBrandScope();
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.toString();
  const applied = useMemo(
    () => readFilters(config, new URLSearchParams(search)),
    [config, search],
  );
  const limit = validPageSize(
    searchParams.get(config.cursor ? "limit" : "size"),
  );
  const baseParams: QueryParams = {
    ...applied,
    brandId,
    [config.cursor ? "limit" : "size"]: limit,
  };
  const key = JSON.stringify([entity, baseParams]);
  const [paging, setPaging] = useState<PagingState>({
    key,
    index: 0,
    cursors: [undefined],
  });
  const activePaging = pagingForQuery(paging, key);
  // Persist every query transition before commit so A → B → A cannot reuse A's cursor.
  if (activePaging !== paging) setPaging(activePaging);
  const cursor = activePaging.cursors[activePaging.index];
  const params: QueryParams = {
    ...baseParams,
    ...(config.cursor ? { cursor } : { page: activePaging.index }),
  };
  const { data, loading, error, reload } = useApi<CursorPage | OffsetPage>(
    `/api/${entity}`,
    params,
  );
  const [cursorNotice, setCursorNotice] = useState<{
    key: string;
    error: unknown;
  }>();

  useEffect(() => {
    if (
      config.cursor &&
      cursor !== undefined &&
      error instanceof ApiError &&
      error.error === "invalid_cursor"
    ) {
      setCursorNotice({ key, error });
      setPaging({ key, index: 0, cursors: [undefined] });
    }
  }, [config.cursor, cursor, error, key]);

  const changeFilters = (values: FilterValues) => {
    const next = new URLSearchParams();
    if (brandId !== undefined) next.set("brandId", String(brandId));
    if (limit !== 50) next.set(config.cursor ? "limit" : "size", String(limit));
    Object.entries(values).forEach(([name, value]) => {
      (Array.isArray(value) ? value : [value]).forEach((item) => {
        if (item.trim()) next.append(name, item.trim());
      });
    });
    setSearchParams(next);
  };
  const changePageSize = (value: number) => {
    const next = new URLSearchParams(searchParams);
    next.set(config.cursor ? "limit" : "size", String(value));
    next.delete("cursor");
    next.delete("page");
    setSearchParams(next);
  };
  const firstPage = () => {
    if (activePaging.index === 0) reload();
    else setPaging({ key, index: 0, cursors: [undefined] });
  };
  const nextPage = () => {
    if (config.cursor) {
      const nextCursor = (data as CursorPage | undefined)?.nextCursor;
      if (!nextCursor) return;
      setPaging({
        key,
        index: activePaging.index + 1,
        cursors: [
          ...activePaging.cursors.slice(0, activePaging.index + 1),
          nextCursor,
        ],
      });
    } else {
      setPaging({ ...activePaging, index: activePaging.index + 1 });
    }
  };
  const rows = data?.items || [];
  const offset = !config.cursor && data ? (data as OffsetPage) : undefined;
  const hasNext = config.cursor
    ? !!(data as CursorPage | undefined)?.nextCursor
    : !!offset && (activePaging.index + 1) * limit < offset.totalItems;
  const sizes = Array.from(new Set([25, 50, 100, 200, limit])).sort(
    (left, right) => left - right,
  );

  return (
    <Stack gap={2} sx={{ p: { xs: 2, md: 3 }, minWidth: 0 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
      >
        <Box>
          <Typography variant="h5" fontWeight={600}>
            {config.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            {brandId === undefined
              ? "All accessible brands"
              : `Brand ${brandId}`}
            {entity === "transactions"
              ? " · Amounts are raw integer values returned by the API"
              : ""}
          </Typography>
        </Box>
        <Button variant="outlined" onClick={reload} disabled={loading}>
          Refresh
        </Button>
      </Stack>
      <Filters
        config={config}
        applied={applied}
        onApply={changeFilters}
        onReset={() => changeFilters({})}
        apiError={error}
      />
      {cursorNotice?.key === key && (
        <Box>
          <Alert
            severity="info"
            onClose={() => setCursorNotice(undefined)}
            sx={{ mb: 1 }}
          >
            The cursor is no longer valid. The list restarted from the first
            page.
          </Alert>
          <ApiErrorPanel error={cursorNotice.error} onRestart={firstPage} />
        </Box>
      )}
      {!!error &&
        !(
          config.cursor &&
          cursor !== undefined &&
          error instanceof ApiError &&
          error.error === "invalid_cursor"
        ) && (
          <ApiErrorPanel
            error={error}
            onRetry={reload}
            onRestart={activePaging.index > 0 ? firstPage : undefined}
          />
        )}
      <Paper variant="outlined" sx={{ overflow: "hidden" }}>
        <Box sx={{ height: 3 }}>{loading && <LinearProgress />}</Box>
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small" aria-label={config.title} sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow>
                {config.columns.map((column) => (
                  <TableCell key={column} sx={{ whiteSpace: "nowrap" }}>
                    {fieldLabel(column)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && !error && rows.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={config.columns.length}
                    align="center"
                    sx={{ py: 7 }}
                  >
                    <Typography color="text.secondary">
                      No {config.title.toLowerCase()} match these filters.
                    </Typography>
                    <Button onClick={() => changeFilters({})} sx={{ mt: 1 }}>
                      Clear filters
                    </Button>
                  </TableCell>
                </TableRow>
              )}
              {loading && rows.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={config.columns.length}
                    align="center"
                    sx={{ py: 7 }}
                  >
                    <CircularProgress
                      size={24}
                      aria-label={`Loading ${config.title.toLowerCase()}`}
                    />
                  </TableCell>
                </TableRow>
              )}
              {rows.map((record, index) => (
                <TableRow
                  key={`${String(record.brandId)}:${String(record[config.idField])}:${index}`}
                  hover
                >
                  {config.columns.map((column) => (
                    <TableCell
                      key={column}
                      sx={{
                        verticalAlign: "middle",
                        whiteSpace: "nowrap",
                        minWidth: 90,
                      }}
                    >
                      <EntityValue
                        entity={entity}
                        field={column}
                        record={record}
                        brandId={brandId}
                        linkId
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
          sx={{ p: 2, borderTop: 1, borderColor: "divider" }}
          gap={2}
        >
          <Stack direction="row" alignItems="center" gap={2}>
            <TextField
              select
              size="small"
              label="Rows per page"
              value={limit}
              onChange={(event) => changePageSize(Number(event.target.value))}
              sx={{ width: 140 }}
            >
              {sizes.map((size) => (
                <MenuItem value={size} key={size}>
                  {size}
                </MenuItem>
              ))}
            </TextField>
            <Typography variant="body2" color="text.secondary">
              Page {activePaging.index + 1}
              {offset ? ` · ${offset.totalItems} total` : ""}
            </Typography>
          </Stack>
          <Stack direction="row" gap={1}>
            <Button
              onClick={firstPage}
              disabled={loading || activePaging.index === 0}
            >
              First
            </Button>
            <Button
              onClick={() =>
                setPaging({ ...activePaging, index: activePaging.index - 1 })
              }
              disabled={loading || activePaging.index === 0}
            >
              Previous
            </Button>
            <Button
              onClick={nextPage}
              disabled={loading || !!error || !hasNext}
              variant="outlined"
            >
              Next
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );
}

function EntityDetail({ entity }: { entity: EntityName }) {
  const config = entityConfig[entity];
  const { id = "" } = useParams<{ id: string }>();
  const { brandId } = useBrandScope();
  const { user } = useSyncExternalStore(
    authStore.subscribe,
    authStore.getSnapshot,
    authStore.getSnapshot,
  );
  const platformAdmin =
    user?.role === "PLATFORM_ADMIN" || !!user?.platformAdmin;
  const invalidPlayerId = entity === "players" && id.includes("/");
  const needsBrand =
    entity === "players" && platformAdmin && brandId === undefined;
  const blocked = invalidPlayerId || needsBrand || !id;
  const { data, loading, error, reload } = useApi<EntityRecord>(
    blocked ? null : `/api/${entity}/${encodeURIComponent(id)}`,
    { brandId },
  );
  const actualBrand = data ? brandOf(data, brandId) : brandId;
  const fields = data
    ? [
        ...config.fields,
        ...Object.keys(data).filter((field) => !config.fields.includes(field)),
      ]
    : config.fields;
  const playerId = data?.userId ?? id;

  return (
    <Stack gap={2} sx={{ p: { xs: 2, md: 3 }, minWidth: 0 }}>
      <Box>
        <Button
          component={RouterLink}
          to={listLink(entity, { brandId })}
          sx={{ mb: 1 }}
        >
          ← {config.title}
        </Button>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
        >
          <Typography variant="h5" fontWeight={600}>
            {config.singular} details
          </Typography>
          <Button
            variant="outlined"
            disabled={blocked || loading}
            onClick={reload}
          >
            Refresh
          </Button>
        </Stack>
        <Box sx={{ mt: 1, color: "text.secondary" }}>
          <DataValue
            value={id}
            label={fieldLabel(config.idField)}
            copy
            maxWidth={640}
          />
        </Box>
      </Box>
      {invalidPlayerId && (
        <Alert severity="warning">
          This user ID contains a slash. The Backoffice API rejects slashes in
          player detail paths. Open the Players list and filter by this User ID
          to view the player record.
        </Alert>
      )}
      {needsBrand && (
        <Alert severity="info">
          Select a specific brand in the header to view this player. Player IDs
          can be shared by different brands.
        </Alert>
      )}
      {!!error && <ApiErrorPanel error={error} onRetry={reload} />}
      {loading && <LinearProgress />}
      {data && !blocked && (
        <>
          {entity === "players" && (
            <Stack direction="row" gap={1} flexWrap="wrap">
              <Button
                component={RouterLink}
                to={listLink("sessions", {
                  userId: String(playerId),
                  brandId: actualBrand,
                })}
                variant="outlined"
              >
                View sessions
              </Button>
              <Button
                component={RouterLink}
                to={listLink("transactions", {
                  userId: String(playerId),
                  brandId: actualBrand,
                })}
                variant="outlined"
              >
                View transactions
              </Button>
            </Stack>
          )}
          {entity === "transactions" && (
            <Alert severity="info">
              Amount and new balance are displayed as the raw integer values
              returned by the API.
            </Alert>
          )}
          <Paper variant="outlined">
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table size="small" aria-label={`${config.singular} details`}>
                <TableBody>
                  {fields.map((field) => (
                    <TableRow key={field}>
                      <TableCell
                        component="th"
                        scope="row"
                        sx={{
                          verticalAlign: "top",
                          width: { xs: "40%", md: 260 },
                          color: "text.secondary",
                        }}
                      >
                        {fieldLabel(field)}
                      </TableCell>
                      <TableCell
                        sx={{ verticalAlign: "middle", whiteSpace: "nowrap" }}
                      >
                        <EntityValue
                          entity={entity}
                          field={field}
                          record={data}
                          brandId={actualBrand}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </Stack>
  );
}

export const TransactionsPage = () => <EntityList entity="transactions" />;
export const SessionsPage = () => <EntityList entity="sessions" />;
export const PlayersPage = () => <EntityList entity="players" />;
export const GamesPage = () => <EntityList entity="games" />;
export const TransactionDetail = () => <EntityDetail entity="transactions" />;
export const SessionDetail = () => <EntityDetail entity="sessions" />;
export const PlayerDetail = () => <EntityDetail entity="players" />;
export const GameDetail = () => <EntityDetail entity="games" />;
